/** Peer protocol messages. Everything except the handshake travels sealed. */

import type { HandshakeMessage } from './noise.ts';
import type { SignedVertex, VertexId } from './tx.ts';
import type { RelayDescriptor } from './routing.ts';

export interface Envelope {
  /** Encrypted frame produced by SecureChannel.seal. */
  t: 'f';
  n: string;
  c: string;
}

export type OuterMessage = HandshakeMessage | Envelope;

export interface MsgPing { t: 'ping'; ts: number; nodes: number }
export interface MsgPong { t: 'pong'; ts: number; nodes: number }

/** Inventory announcement - ids only, so gossip is cheap. */
export interface MsgHave { t: 'have'; ids: VertexId[] }
/** Ask for the bodies of ids we do not hold. */
export interface MsgWant { t: 'want'; ids: VertexId[] }
export interface MsgVertices { t: 'vertices'; vertices: SignedVertex[] }

/** Consensus poll. The whole of the voting protocol is these two messages. */
export interface MsgQuery { t: 'query'; rid: number; ids: VertexId[] }
export interface MsgQueryReply { t: 'qreply'; rid: number; answers: Record<VertexId, boolean> }

/** Frontier sync for a node that has just come online. */
export interface MsgTips { t: 'tips'; rid: number }
export interface MsgTipsReply { t: 'tipsreply'; rid: number; tips: VertexId[]; height: number }

/** Ask for everything after a checkpoint - used by a cold-starting node. */
export interface MsgSync { t: 'sync'; rid: number; since: number; limit: number }
export interface MsgSyncReply { t: 'syncreply'; rid: number; vertices: SignedVertex[]; more: boolean; cursor: number }

/** Peer and relay exchange. This is how the network heals around a block. */
export interface MsgPeers { t: 'peers'; relays: RelayDescriptor[] }

export type InnerMessage =
  | MsgPing | MsgPong
  | MsgHave | MsgWant | MsgVertices
  | MsgQuery | MsgQueryReply
  | MsgTips | MsgTipsReply
  | MsgSync | MsgSyncReply
  | MsgPeers;

export const MAX_VERTICES_PER_MESSAGE = 256;
export const MAX_IDS_PER_MESSAGE = 2048;
